import {t} from '@lingui/macro';
import {useFlag} from '@unleash/proxy-client-react';
import theme from 'config/theme';
import {Picker} from 'extensions/viewport/Picker';
import {api} from 'fast-sdk';
import {Input} from 'interface/base/Input';
import {Textarea} from 'interface/base/Textarea';
import {Loading} from 'interface/common/Loading';
import {PageTitle} from 'interface/stacks/settings/base/PageTitle';
import {timezoneOptions} from 'interface/stacks/settings/base/consts';
import {useCallback, useEffect, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {useToast} from 'react-native-toast-notifications';
import {useDispatch} from 'react-redux';
import {useNavigate, useParams} from 'react-router';
import settings from 'store/slices/settings';
import user from 'store/slices/user';
import type {Organization as OrganizationType} from 'store/slices/user/types';
import type {LoadingStates, OrganizationFormData, Params} from '../../types';
import {createSocialLinksFromOrg, validDomain} from '../../utils';
import {AddLogo} from './AddLogo';
import {OrganizationLinks} from './OrganizationLinks';
import {SectionItem} from './SectionItem';

const DESCRIPTION_MAX_LENGTH = 1000;

export function Organization() {
  const {subdomain: paramSubdomain} = useParams<Params>();

  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const showTimeZone = useFlag('show-time-zone');

  const [currentOrganizationDetails, setCurrentOrganizationDetails] =
    useState<OrganizationType>();

  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    timezone: '',
    subdomain: '',
    emailDomain: '',
    description: '',
  });

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    page: false,
    emailDomains: false,
    name: false,
    subdomain: false,
    description: false,
  });

  const updateLoadingState = (key: keyof LoadingStates, value: boolean) => {
    setLoadingStates(prev => ({...prev, [key]: value}));
  };

  const updateFormField = (
    field: keyof OrganizationFormData,
    value: string,
  ) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  const showSuccessToast = (message: string) => {
    toast.show(message, {type: 'success'});
  };

  const showErrorToast = (message: string) => {
    toast.show(message, {type: 'danger'});
  };

  const updateOrganizationInStore = useCallback(
    (org: OrganizationType) => {
      dispatch(user.actions.setSelectedOrganization({org}));
    },
    [dispatch],
  );

  const getOrgDetails = useCallback(async () => {
    if (!paramSubdomain) return;

    updateLoadingState('page', true);

    try {
      const {result, org} =
        await api.organization.getOrganizationDetails(paramSubdomain);

      if (result && org) {
        setCurrentOrganizationDetails(org);

        setFormData({
          name: org.name || '',
          subdomain: org.domain || '',
          emailDomain: org.perm_auth_domains || '',
          description: org.description || '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        const socials = createSocialLinksFromOrg(org);
        dispatch(settings.actions.setOrganizationSocialLinks({links: socials}));
      }
    } catch (err) {
      showErrorToast(
        'Getting the organization faced error please try again later',
      );
    } finally {
      updateLoadingState('page', false);
    }
  }, [paramSubdomain, dispatch]);

  const updateOrganization = async (
    identifier: string,
    updateData: Partial<OrganizationType>,
    loadingKey: keyof LoadingStates,
    successMessage: string,
  ) => {
    updateLoadingState(loadingKey, true);

    try {
      const res = await api.organization.updateOrganization(
        identifier,
        updateData,
      );

      if (res?.result) {
        const org = await api.organization.getOrganizationDetails(
          formData.subdomain,
        );
        if (org?.result) {
          updateOrganizationInStore(org.org);
          dispatch(user.actions.updateOrganization({org: org.org}));
          if (loadingKey === 'subdomain') {
            navigate(`/settings/organization/${formData.subdomain}`);
          }
          showSuccessToast(successMessage);
        }
      }
    } catch (err) {
      showErrorToast(
        `Changing ${loadingKey} faced error please try again later`,
      );
    } finally {
      updateLoadingState(loadingKey, false);
    }
  };

  const changeOrgName = async () => {
    if (
      !currentOrganizationDetails ||
      formData.name === currentOrganizationDetails.name
    ) {
      return;
    }

    if (!formData.name) {
      showErrorToast(t`Please fill the name`);
      return;
    }

    await updateOrganization(
      formData.subdomain,
      {name: formData.name},
      'name',
      t`Organization name is successfully changed`,
    );
  };

  const changeOrgSubdomain = async () => {
    if (
      !currentOrganizationDetails ||
      formData.subdomain === currentOrganizationDetails.domain
    ) {
      return;
    }

    if (!formData.subdomain) {
      showErrorToast(t`Please fill the subdomain`);
      return;
    }

    await updateOrganization(
      currentOrganizationDetails.id,
      {domain: formData.subdomain},
      'subdomain',
      t`Organization subdomain is successfully changed`,
    );
  };

  const changeEmailDomain = async () => {
    if (
      !currentOrganizationDetails ||
      formData.emailDomain === currentOrganizationDetails.perm_auth_domains
    ) {
      return;
    }

    if (!validDomain(formData.emailDomain)) {
      showErrorToast(t`Please enter a valid domain`);
      return;
    }

    await updateOrganization(
      formData.subdomain,
      {perm_auth_domains: formData.emailDomain},
      'emailDomains',
      t`Organization authorized email domain is successfully changed`,
    );
  };

  const changeOrgDescription = async () => {
    if (
      !currentOrganizationDetails ||
      formData.description === currentOrganizationDetails.description
    ) {
      return;
    }

    if (!formData.description) return;

    await updateOrganization(
      formData.subdomain,
      {description: formData.description},
      'description',
      t`Organization description is successfully changed`,
    );
  };

  useEffect(() => {
    getOrgDetails();
  }, [getOrgDetails]);

  const renderInputWithLoading = (
    value: string,
    onChangeValue: (value: string) => void,
    onBlur: () => void,
    isLoading: boolean,
    ariaLabel: string,
    placeholder?: string,
    error?: boolean,
    errorMessage?: string,
  ) => (
    <View style={styles.inputContainer}>
      <Input
        overrides={{Root: {style: styles.inputRoot}}}
        value={value}
        onChangeValue={onChangeValue}
        aria-label={ariaLabel}
        onBlurFn={onBlur}
        disabled={isLoading}
        placeholder={placeholder}
        error={error}
        errorMessage={errorMessage}
      />
      {isLoading && <Loading size="small" />}
    </View>
  );

  if (loadingStates.page) {
    return (
      <ScrollView style={styles.root}>
        <View style={styles.loadingContainer}>
          <Loading />
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={styles.root}>
      <View>
        <PageTitle text={t`General Settings`} />

        <SectionItem
          title={t`Subdomain`}
          content={t`Your organization subdomain is where you and your teammates will log in and also where your files will be shared from.`}
          sectionElement={renderInputWithLoading(
            formData.subdomain,
            value => updateFormField('subdomain', value),
            changeOrgSubdomain,
            loadingStates.subdomain,
            t`Organization Domain`,
          )}
        />

        <SectionItem
          title={t`Authorized Email Domain`}
          content={t`Control who can join your organization by specifying email domains to approve sign-ups and streamline invites.`}
          sectionElement={renderInputWithLoading(
            formData.emailDomain,
            value => updateFormField('emailDomain', value),
            changeEmailDomain,
            loadingStates.emailDomains,
            'Authorized Email Domain',
            t`Enter a domain...`,
            !validDomain(formData.emailDomain),
            'Please enter a valid domain',
          )}
        />

        {showTimeZone && (
          <SectionItem
            title={t`Time Zone`}
            content={t`Defaults to the location of the teammate who set up this workspace. Changing it will affect time-dependent features across fast.io.`}
            sectionElement={
              <Picker
                style={styles.dropdown}
                selectedValue={formData.timezone}
                onValueChange={value => updateFormField('timezone', value)}>
                {timezoneOptions.map(item => (
                  <Picker.Item
                    key={item.value}
                    value={item.value}
                    label={item.title}
                  />
                ))}
              </Picker>
            }
          />
        )}
      </View>

      <View>
        <PageTitle text={t`Branding and Customization`} />

        <SectionItem
          title={t`Organization Name`}
          content={t`Customize Fast.io by changing the name of your organization.`}
          sectionElement={renderInputWithLoading(
            formData.name,
            value => updateFormField('name', value),
            changeOrgName,
            loadingStates.name,
            t`Organization Name`,
          )}
        />

        <SectionItem
          title={t`Organization Logo`}
          content={t`Upload your logo to customize Fast.io for your organization.`}
          sectionElement={<AddLogo />}
        />

        <SectionItem
          title={t`Organization Description`}
          content={t`Add an organization description to display on your shared link pages.`}
          sectionElement={
            <View style={styles.inputContainer}>
              <Textarea
                overrides={{TextArea: {style: styles.textarea}}}
                value={formData.description}
                onChangeValue={value => updateFormField('description', value)}
                placeholder={t`Enter organization description...`}
                disabled={loadingStates.description}
                onBlurFn={changeOrgDescription}
                maxLength={DESCRIPTION_MAX_LENGTH}
              />
              {loadingStates.description && <Loading size="small" />}
            </View>
          }
        />

        <OrganizationLinks getOrgDetails={getOrgDetails} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    paddingVertical: 45,
    paddingHorizontal: 50,
    position: 'relative',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  textarea: {
    width: 350,
    height: 110,
  },
  dropdown: {
    height: 36,
    width: 350,
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    backgroundColor: theme.colors.neutral.$13,
    border: 0,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  inputRoot: {
    width: 350,
    marginBottom: 0,
  },
});
